import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Plus, Pencil, Trash2, Sparkles, ExternalLink } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";

export default function AdminBlogListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/articles")
      .then((r) => setItems(r.data || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Bu yazıyı silmek istediğine emin misin?")) return;
    try {
      await adminApi.delete(`/admin/articles/${id}`);
      setItems((arr) => arr.filter((a) => a.id !== id));
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const translate = async (id) => {
    try {
      await adminApi.post(`/admin/articles/${id}/translate`);
      alert("Çeviri arka planda başlatıldı.");
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-blog-list-page" className="space-y-8">
      <Helmet><title>Blog · Admin</title></Helmet>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-brand-pink-deep font-bold">İçerik</span>
          <h1 className="mt-2 font-display text-display-2 font-bold text-ink">Blog yazıları</h1>
        </div>
        <Link to="/admin/blog/new" data-testid="admin-new-article-btn"
          className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-brand-pink-deep transition-colors">
          <Plus className="h-4 w-4" />
          Yeni yazı
        </Link>
      </div>

      <div className="rounded-2xl border border-mist bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink/55 text-sm">Yükleniyor…</div>
        ) : err ? (
          <div className="p-10 text-center text-red-700 text-sm">{err}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink/55 text-sm">
            Henüz yazı yok. <Link to="/admin/blog/new" className="text-brand-pink-deep underline">İlk yazıyı oluştur</Link>.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-mist/40 text-ink/65 text-[0.65rem] uppercase tracking-[0.18em] font-bold">
              <tr>
                <th className="text-start px-5 py-3">Başlık</th>
                <th className="text-start px-3 py-3 hidden md:table-cell">Kategori</th>
                <th className="text-start px-3 py-3">Durum</th>
                <th className="text-start px-3 py-3 hidden sm:table-cell">Tarih</th>
                <th className="text-end px-5 py-3">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-mist">
              {items.map((a, idx) => {
                const tr = a.translations?.tr || {};
                return (
                  <tr key={a.id || `${a.slug}-${idx}`} data-testid={`admin-article-row-${a.slug}`} className="hover:bg-mist/30">
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-ink line-clamp-1">{tr.title || a.slug}</p>
                      <p className="text-xs text-ink/55 mt-0.5">/{a.slug}</p>
                    </td>
                    <td className="px-3 py-3.5 hidden md:table-cell">
                      <span className="text-xs uppercase tracking-[0.16em] text-ink/55">{a.category}</span>
                    </td>
                    <td className="px-3 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.16em] font-bold
                        ${a.status === "published" ? "bg-green-100 text-green-700" : "bg-mist text-ink/60"}`}>
                        {a.status === "published" ? "Yayında" : "Taslak"}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 hidden sm:table-cell text-xs text-ink/55">
                      {a.published_at ? new Date(a.published_at).toLocaleDateString("tr-TR") : "—"}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <Link to={`/tr/blog/${a.slug}`} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-mist text-ink/60" title="Önizle">
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                        <button onClick={() => translate(a.id)} className="p-2 rounded-lg hover:bg-mist text-ink/60" title="AI Çevir">
                          <Sparkles className="h-4 w-4" />
                        </button>
                        <Link to={`/admin/blog/${a.id}/edit`} className="p-2 rounded-lg hover:bg-mist text-ink/60" title="Düzenle">
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <button onClick={() => remove(a.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Sil"
                          data-testid={`admin-delete-${a.slug}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
